import type { Translation } from '../../types';
import type { admin as en } from '../en/admin';

export const admin: Translation<typeof en> = {
  area: 'Quản trị',

  note: {
    audited:
      'Mọi việc thực hiện trong khu vực này đều được ghi vào nhật ký kiểm toán: ai làm, làm lúc nào và lý do họ đưa ra.',
    noSecrets:
      'Khu vực này không bao giờ hiển thị thông tin bí mật — không mật khẩu, không mã MFA, không token phiên, không khoá API.',
    fourEyesTitle: 'Hai người, một thay đổi',
    fourEyesBody:
      'Những thay đổi rủi ro nhất cần hai người: một người đề nghị thay đổi và nêu lý do, một người khác đồng ý. Không ai được đồng ý với đề nghị của chính mình — hệ thống sẽ từ chối. Chưa có người đồng ý thì chưa có gì xảy ra.',
    selfBlocked:
      'Bạn là người đề nghị thay đổi này nên bạn không thể là người đồng ý. Hãy nhờ một đồng nghiệp cũng có quyền này.',
    immutable: 'Nhật ký này không bao giờ có thể sửa hoặc xoá. Các bản ghi được giữ đúng như lúc được ghi.',
    keyOnce:
      'Bí mật của khoá chỉ hiển thị một lần, cho người tạo ra khoá. Không bao giờ đọc lại được. Nếu bị mất, hãy thu hồi khoá và cấp khoá mới.',
  },

  filter: {
    search: 'Tìm kiếm',
    reset: 'Xoá bộ lọc',
    refresh: 'Làm mới',
    any: 'Tất cả',
  },

  scope: {
    global: 'Toàn bộ nền tảng',
    customer: 'Khách hàng',
  },

  staffStatus: {
    ACTIVE: 'Đang hoạt động',
    SUSPENDED: 'Tạm ngưng',
    DISABLED: 'Đã vô hiệu hoá',
  },

  userKind: {
    CLIENT: 'Khách hàng',
    STAFF: 'Nhân viên',
    CUSTOMER: 'Khách hàng',
  },

  staff: {
    title: 'Tài khoản nhân viên',
    subtitle:
      'Tất cả những người có thể đăng nhập Control Center, các vai trò họ giữ và việc đăng nhập của họ có được bảo vệ hay không.',
    loadError: 'Không tải được danh sách tài khoản nhân viên',
    empty: 'Không có tài khoản nhân viên nào khớp với bộ lọc này.',
    count: '{{count}} tài khoản',
    searchPlaceholder: 'Tên hoặc địa chỉ e-mail',
    filterStatus: 'Trạng thái tài khoản',
    filterRole: 'Vai trò',
    filterLocked: 'Đăng nhập',
    lockedOnly: 'Đang bị tạm khoá',
    unlockedOnly: 'Đăng nhập được',
    col: {
      person: 'Người',
      signIn: 'Đăng nhập',
      roles: 'Vai trò',
      lastLogin: 'Đăng nhập lần cuối',
      sessions: 'Phiên đang mở',
      state: 'Trạng thái',
    },
    mfa: {
      on: 'Đã có MFA',
      missing: 'Chưa thiết lập MFA',
      missingHint:
        'Tài khoản này buộc phải qua MFA nhưng chưa thiết lập ứng dụng xác thực nên không thể đăng nhập.',
      notRequired: 'Không yêu cầu MFA',
    },
    locked: 'Đang bị tạm khoá',
    lockedUntil: 'Tạm khoá đến {{time}}',
    failedAttempts: '{{count}} lần đăng nhập thất bại',
    neverSignedIn: 'Chưa bao giờ',
    noRoles: 'Không có vai trò',
    moreRoles: '+{{count}} vai trò nữa',
    open: 'Mở {{name}}',
    stat: {
      total: 'Tài khoản nhân viên',
      mfaMissing: 'Chưa có MFA',
      mfaMissingHint: 'Không thể đăng nhập cho đến khi thiết lập ứng dụng xác thực.',
      locked: 'Đang bị tạm khoá',
      lockedHint: 'Quá nhiều lần đăng nhập thất bại.',
      sessions: 'Phiên đang mở',
      sessionsHint: 'Các phiên hiện vẫn còn hiệu lực.',
    },
  },

  member: {
    loadError: 'Không tải được tài khoản nhân viên này',
    notFound: 'Không có tài khoản nhân viên nào mang mã này.',
    overview: 'Tài khoản',
    email: 'Địa chỉ e-mail',
    state: 'Trạng thái tài khoản',
    signIn: 'Bảo vệ đăng nhập',
    created: 'Ngày tạo tài khoản',
    lastLogin: 'Đăng nhập lần cuối',
    failed: 'Số lần đăng nhập thất bại',
    id: 'ID',
    roles: 'Vai trò',
    rolesHint: 'Người này được phép làm gì và trong phạm vi nào.',
    noRoles: 'Tài khoản này không giữ vai trò nào nên không đọc được và không thay đổi được gì.',
    roleCol: { role: 'Vai trò', scope: 'Áp dụng cho', granted: 'Được cấp' },
    sessions: 'Phiên đang mở',
    sessionsHint: 'Các phiên vẫn còn hiệu lực. Không thể đọc lại hay dùng lại một phiên từ đây.',
    noSessions: 'Không có phiên nào đang mở.',
    sessionCol: { started: 'Bắt đầu', expires: 'Hết hiệu lực', mfa: 'MFA', from: 'Từ', device: 'Thiết bị' },
    mfaVerified: 'Đã xác thực',
    mfaNotVerified: 'Chưa xác thực',
    recentAudit: 'Hoạt động gần đây',
    recentAuditHint: 'Những việc gần nhất người này đã làm.',
    noAudit: 'Chưa ghi nhận hoạt động nào của người này.',
    ownRequests: 'Đang chờ đồng nghiệp',
    ownRequestsHint: 'Những thay đổi người này đề nghị. Họ không thể tự đồng ý bất kỳ đề nghị nào.',
    noOwnRequests: 'Không có đề nghị nào đang chờ.',
    allActivity: 'Toàn bộ hoạt động của người này',
  },

  roles: {
    title: 'Vai trò & quyền',
    subtitle: 'Mỗi vai trò được phép làm gì và ai đang giữ vai trò đó.',
    loadError: 'Không tải được danh sách vai trò',
    empty: 'Chưa có vai trò nào được định nghĩa.',
    count: '{{count}} vai trò',
    builtIn: 'Có sẵn',
    builtInHint: 'Đi kèm nền tảng. Phần mềm tham chiếu vai trò này theo tên.',
    col: {
      role: 'Vai trò',
      permissions: 'Được phép',
      sensitive: 'Cần MFA',
      fourEyes: 'Cần người thứ hai',
      members: 'Người đang giữ',
    },
    permissionCount: '{{count}} quyền',
    memberCount: '{{count}} người',
    staffOf: '{{count}} nhân viên',
    open: 'Mở vai trò {{role}}',
    stat: {
      roles: 'Vai trò',
      sensitive: 'Vai trò có quyền cần MFA',
      fourEyes: 'Vai trò có quyền cần hai người',
      grants: 'Lượt cấp vai trò cho nhân viên',
    },
  },

  role: {
    loadError: 'Không tải được vai trò này',
    notFound: 'Không có vai trò nào mang tên này.',
    about: 'Vai trò',
    code: 'Tên vai trò',
    description: 'Dùng để làm gì',
    permissions: 'Được phép',
    permissionsHint: 'Nhóm theo phần nghiệp vụ mà mỗi quyền chi phối.',
    noPermissions: 'Vai trò này không cấp quyền nào.',
    alsoNeeds: 'Yêu cầu thêm',
    needsMfa: 'Cần MFA',
    needsMfaHint: 'Chỉ dùng được trong phiên đã xác nhận bằng mã từ ứng dụng xác thực.',
    needsTwo: 'Cần người thứ hai',
    needsTwoHint: 'Một nhân viên đề nghị, một nhân viên khác đồng ý, trước khi có bất kỳ thay đổi nào.',
    members: 'Người đang giữ',
    membersHint: 'Tất cả những người đang giữ vai trò này và phạm vi áp dụng.',
    noMembers: 'Không ai giữ vai trò này.',
    memberCol: {
      person: 'Người',
      kind: 'Tài khoản',
      scope: 'Áp dụng cho',
      state: 'Trạng thái',
      granted: 'Được cấp',
    },
    group: {
      support: 'Hỗ trợ khách hàng',
      domain: 'Tên miền',
      subject: 'Chủ thể',
      identity_document: 'Giấy tờ định danh',
      dossier: 'Hồ sơ',
      registry: 'Cơ quan đăng ký',
      billing: 'Thanh toán & sổ cái',
      catalog: 'Danh mục sản phẩm',
      subscription: 'Dịch vụ đã bán',
      tax: 'Thuế',
      einvoice: 'Hoá đơn điện tử',
      auction: 'Đấu giá',
      compliance: 'Tuân thủ',
      disclosure: 'Cung cấp dữ liệu',
      abuse: 'Lạm dụng',
      dispute: 'Tranh chấp',
      dns: 'Dịch vụ DNS',
      escrow: 'Lưu ký dữ liệu',
      vnnic: 'VNNIC',
      customer: 'Tài khoản khách hàng',
      audit: 'Nhật ký kiểm toán',
      ops: 'Vận hành',
      admin: 'Quản trị',
      account: 'Tài khoản (khách)',
      api_credential: 'Khoá API',
      reporting_key: 'API báo cáo',
      personal_data: 'Dữ liệu cá nhân',
      addon: 'Tiện ích',
      approval: 'Phê duyệt',
      bulk: 'Tác vụ hàng loạt',
      business_registry: 'Tra cứu doanh nghiệp',
      integration: 'Kết nối & tích hợp',
      lookup: 'Tra cứu tên miền',
      name_change: 'Đổi tên chủ thể',
      notify: 'Thông báo & e-mail',
      order: 'Đơn hàng',
      pricing: 'Bảng giá',
      promo_code: 'Mã khuyến mại',
      reference: 'Danh mục tham chiếu',
      regulatory_report: 'Báo cáo cơ quan quản lý',
      service: 'Dịch vụ',
      tld: 'TLD',
      wallet: 'Ví',
      webhook: 'Webhook',
      work_item: 'Việc cần xử lý',
    },
  },

  roleName: {
    administrator: 'Quản trị nền tảng',
    auditor: 'Kiểm toán (chỉ đọc)',
    billing: 'Nghiệp vụ thanh toán',
    compliance: 'Cán bộ tuân thủ',
    customer: 'Khách hàng lẻ',
    ops_admin: 'Quản trị vận hành',
    ops_engineer: 'Kỹ sư vận hành',
    ops_viewer: 'Theo dõi vận hành',
    registry_ops: 'Vận hành kết nối cơ quan đăng ký',
    security: 'Bộ phận an ninh',
    support: 'Hỗ trợ khách hàng',
    client_customer_owner: 'Khách hàng — chủ tài khoản',
    client_customer_admin: 'Khách hàng — quản trị',
    client_customer_billing: 'Khách hàng — thanh toán',
    client_customer_technical: 'Khách hàng — kỹ thuật',
    client_customer_read_only: 'Khách hàng — chỉ xem',
    client_customer_key: 'Khoá API của khách hàng (theo phạm vi)',
    client_customer_key_legacy: 'Khoá API cũ của khách hàng (không phạm vi)',
  },

  permission: {
    support_create: 'Mở phiếu hỗ trợ',
    support_knowledge_manage: 'Quản lý kho tri thức',
    support_manage: 'Quản lý phiếu hỗ trợ, phòng ban và thời hạn xử lý',
    support_read: 'Xem phiếu hỗ trợ',
    support_reply: 'Trả lời phiếu hỗ trợ',
    domain_read: 'Đọc tên miền trong phạm vi',
    domain_write: 'Đăng ký, gia hạn và cập nhật tên miền trong phạm vi',
    domain_ownership_change: 'Đổi chủ thể đăng ký của tên miền',
    domain_ownership_override: 'Cưỡng chế đổi chủ thể bằng thao tác thủ công',
    domain_transfer_override: 'Cưỡng chế chuyển nhà đăng ký bằng thao tác thủ công',
    domain_authinfo_view: 'Xem mã chuyển nhượng của tên miền',
    subject_read: 'Đọc chủ thể, dữ liệu cá nhân được che',
    subject_write: 'Tạo chủ thể và bản ghi chủ thể mới',
    identity_document_view: 'Xem giấy tờ định danh và số định danh đầy đủ',
    dossier_read: 'Đọc hồ sơ',
    dossier_review: 'Thẩm định và hoàn tất hồ sơ',
    registry_transaction_read: 'Đọc giao dịch với cơ quan đăng ký, giá trị nhạy cảm được ẩn',
    registry_payload_raw_view: 'Xem nội dung gốc trao đổi với cơ quan đăng ký',
    registry_credential_manage: 'Quản lý thông tin xác thực và chứng thư số với cơ quan đăng ký',
    registry_reconcile: 'Xử lý sai lệch phát hiện khi đối soát với cơ quan đăng ký',
    billing_read: 'Đọc thanh toán và sổ cái',
    billing_adjust: 'Điều chỉnh số dư',
    einvoice_issue: 'Phát hành hoá đơn điện tử, gửi lại và tra cứu theo khoá',
    einvoice_manage: 'Đề nghị và thực hiện điều chỉnh, thay thế, huỷ hoá đơn điện tử (người thứ hai duyệt)',
    billing_adjust_large: 'Điều chỉnh số dư với giá trị lớn',
    auction_read: 'Đọc phiên đấu giá, lượt đặt giá, yêu cầu đặt giữ tên miền và tên miền dự kiến rơi',
    auction_manage: 'Tạo, huỷ phiên đấu giá và lên lịch tên miền rơi',
    auction_result_override: 'Can thiệp kết quả phiên đấu giá',
    compliance_read: 'Đọc vụ việc tuân thủ và tình trạng của chúng',
    compliance_override: 'Can thiệp một quyết định tuân thủ nghiêm trọng',
    compliance_legal_unlock: 'Gỡ khoá pháp lý',
    disclosure_read: 'Đọc yêu cầu cung cấp dữ liệu đăng ký',
    disclosure_manage: 'Tiếp nhận và phân loại yêu cầu cung cấp dữ liệu đăng ký',
    disclosure_decide: 'Chấp thuận hoặc từ chối yêu cầu cung cấp dữ liệu đăng ký',
    disclosure_release: 'Bàn giao dữ liệu đăng ký không công khai đã được chấp thuận',
    abuse_read: 'Đọc vụ việc lạm dụng, báo cáo, bằng chứng và tác vụ tên miền',
    abuse_manage: 'Mở và phân loại vụ việc lạm dụng, bổ sung bằng chứng, đề nghị tác vụ tên miền',
    dispute_manage: 'Mở, phân xử và đóng tranh chấp tên miền, bao gồm cả khoá pháp lý',
    escrow_read: 'Đọc bản nộp lưu ký dữ liệu, kết quả kiểm tra, lần gửi và biên nhận',
    escrow_manage: 'Kích hoạt hoặc huỷ bản nộp lưu ký và ghi nhận biên nhận của đơn vị lưu ký',
    vnnic_read: 'Đọc đồng bộ, báo cáo và độ phủ VNNIC',
    vnnic_manage: 'Lập và xuất báo cáo VNNIC, ghi nhận kết quả của báo cáo',
    customer_manage: 'Tạo và quản lý tài khoản khách hàng',
    audit_read: 'Đọc nhật ký kiểm toán',
    ops_read: 'Đọc bảng theo dõi vận hành',
    ops_query: 'Truy vấn chỉ số, log và trace',
    ops_admin: 'Quản lý cảnh báo, tạm tắt cảnh báo và sự cố',
    admin_users_manage: 'Quản lý tài khoản nhân viên và vai trò',
    api_credential_manage: 'Cấp và thu hồi khoá API của khách hàng',
    reporting_key_read: 'Xem nơi nhận dữ liệu báo cáo, khoá và lịch sử dùng',
    reporting_key_manage: 'Quản lý nơi nhận dữ liệu báo cáo và khoá',
    personal_data_export: 'Duyệt việc gửi thông tin liên hệ của khách cho nơi nhận dữ liệu',
    dns_tsig_manage: 'Đặt và thay khoá bảo vệ việc sao chép vùng DNS (TSIG); khoá được lưu mã hoá và không hiển thị lại',
    catalog_read: 'Xem danh mục sản phẩm: nhóm, sản phẩm, gói, tuỳ chọn, cách cấp phát, tồn kho và cài đặt quá hạn',
    catalog_manage: 'Sửa danh mục sản phẩm (bản nháp): nhóm, sản phẩm, gói, tuỳ chọn, add-on, cách cấp phát, tồn kho, vị trí, nhóm máy chủ và cài đặt quá hạn',
    catalog_publish: 'Mở bán hoặc ngừng bán một gói và công bố giá mới của gói (trên môi trường thật cần người thứ hai duyệt)',
    subscription_read: 'Xem dịch vụ đã bán: danh sách, chi tiết, các kỳ đã trả, đơn gia hạn, lịch quá hạn và hàng chờ cấp phát',
    subscription_operate: 'Thao tác trên dịch vụ: tạm ngưng và mở lại có lý do, cộng thời hạn tới 31 ngày, xác nhận đã cấp phát thủ công',
    subscription_terminate: 'Yêu cầu huỷ một dịch vụ, ngay hoặc vào cuối kỳ',
    subscription_order: 'Tạo dịch vụ mới cho khách: đặt đơn từ một gói và giữ tiền trên ví của khách',
    subscription_extend_large: 'Cộng thời hạn miễn phí trên 31 ngày cho một dịch vụ',
    pricing_manage: 'Thay đổi giá bán, giá vốn, bảng giá và khuyến mãi',
    tax_manage: 'Công bố, kết thúc thuế suất GTGT và chọn mã thuế mặc định',
    account_manage: 'Khách: quản lý người dùng, lời mời, khoá API và ứng dụng kết nối (chỉ trong phiên đăng nhập)',
    account_read: 'Khách: xem hồ sơ tài khoản',
    addon_read: 'Xem các tiện ích, trạng thái và trang của chúng',
    addon_operate: 'Dùng thao tác trên trang tiện ích và đường dẫn API của tiện ích dành cho nhân viên, chạy lại sự kiện bị tạm giữ',
    addon_manage: 'Cài đặt, bật, tắt, nâng cấp và gỡ tiện ích, chọn cách giữ hay xoá dữ liệu của tiện ích (trên môi trường thật, và khi xoá dữ liệu, cần người thứ hai duyệt)',
    approval_self_approve: 'Tự phê duyệt thay đổi tích hợp của mình khi không có người thứ hai (xác nhận lại TOTP, có ghi kiểm toán)',
    bulk_run: 'Khách: chạy tác vụ hàng loạt',
    business_registry_lookup: 'Tra cứu doanh nghiệp, hộ kinh doanh theo mã số thuế',
    domain_destroy: 'Khách: xoá tên miền',
    domain_protection_manage: 'Bật / tắt bảo vệ tên miền, xác nhận hoặc từ chối thay đổi đang chờ',
    domain_transfer: 'Khách: mã chuyển, trả lời chuyển đi, mở khoá chuyển, đổi chủ thể và đổi tên',
    dossier_write: 'Khách: tệp hồ sơ, bản khai, eKYC',
    integration_manage: 'Thay đổi kết nối registry, kênh TLD và cấu hình VNNIC; kiểm tra kết nối (thay đổi môi trường thật cần người thứ hai duyệt)',
    integration_read: 'Xem kết nối registry, kênh TLD, cấu hình VNNIC, kết quả kiểm tra và lịch sử',
    lookup_registrant_view: 'Xem tên chủ thể khi tra cứu WHOIS tại registry (VNNIC infoDomain)',
    name_change_manage: 'Mở yêu cầu đổi tên, tải tài liệu, ghi nhận đồng ý, gửi hoặc huỷ yêu cầu',
    name_change_requirements_manage: 'Ban hành bộ tài liệu yêu cầu khi đổi tên (theo từng trường hợp)',
    name_change_review: 'Duyệt, từ chối, trả lại yêu cầu đổi tên và ghi nhận VNNIC từ chối',
    notify_inbox_read: 'Khách: xem thông báo',
    notify_manage: 'Thay đổi cấu hình e-mail và mẫu, gửi lại thông báo',
    notify_read: 'Xem cấu hình e-mail, mẫu và nhật ký thông báo',
    order_create: 'Khách: đăng ký, gia hạn, chuyển về, khôi phục, dịch vụ thêm, đặt giá, đặt giữ (trừ vào ví)',
    promo_code_request: 'Xin mã khuyến mại của chương trình miễn phí từ registry (gửi thông tin chủ thể)',
    reference_sync: 'Đồng bộ danh mục tham chiếu (địa giới hành chính)',
    registry_panel_manage: 'Ghi nhận thay đổi đã nhập trên hệ thống quản lý của VNNIC và kiểm tra lại',
    registry_tld_manage: 'Bật / tắt TLD từ danh mục TLD có sẵn',
    regulatory_report_manage: 'Lập, xuất báo cáo cho cơ quan quản lý, ghi nhận kết quả kèm bằng chứng, gửi lại hồ sơ',
    regulatory_report_read: 'Xem báo cáo cho cơ quan quản lý, hồ sơ đã gửi và độ phủ',
    service_manage: 'Khách: tự gia hạn và các cài đặt dịch vụ khác',
    service_read: 'Khách: xem dịch vụ và gói dịch vụ',
    tld_settings_manage: 'Ban hành cài đặt TLD (chuyển đổi, khôi phục, vòng đời hết hạn) và name server đỗ',
    wallet_topup: 'Khách: nạp tiền vào ví (chỉ trong phiên đăng nhập)',
    webhook_manage: 'Thay đổi điểm nhận webhook và khoá bí mật; gửi sự kiện thử; gửi lại',
    webhook_read: 'Xem điểm nhận webhook và nhật ký gửi',
    work_item_manage: 'Giao việc và xử lý mục ngoại lệ (quyết định duyệt cần quyền “Thẩm định và hoàn tất hồ sơ”)',
    work_item_read: 'Xem hàng việc cần xử lý',
  },

  approvalStatus: {
    PENDING: 'Đang chờ',
    APPROVED: 'Đã đồng ý',
    REJECTED: 'Đã từ chối',
    EXPIRED: 'Đã quá hạn',
    EXECUTED: 'Đã thực hiện',
    CANCELLED: 'Đã rút lại',
  },

  approvals: {
    title: 'Phê duyệt',
    subtitle: 'Những thay đổi đang chờ một nhân viên thứ hai đồng ý.',
    loadError: 'Không tải được danh sách phê duyệt',
    empty: 'Không có gì đang chờ quyết định.',
    emptyFiltered: 'Không có đề nghị nào khớp với bộ lọc này.',
    count: '{{count}} đề nghị',
    filterStatus: 'Trạng thái',
    statusLive: 'Đang chờ',
    statusAll: 'Tất cả',
    col: {
      change: 'Thay đổi được đề nghị',
      resource: 'Áp dụng cho',
      requester: 'Người đề nghị',
      age: 'Thời gian chờ',
      status: 'Trạng thái',
      decision: 'Quyết định',
    },
    self: 'Bạn là người đề nghị',
    expiresIn: 'Quá hạn {{when}}',
    expired: 'Đã quá hạn',
    decidedBy: '{{name}} đã quyết định',
    undecided: 'Chưa quyết định',
    open: 'Mở đề nghị này',
    review: 'Xem xét',
    stat: {
      waiting: 'Đang chờ quyết định',
      yours: 'Bạn đề nghị',
      yoursHint: 'Cần một đồng nghiệp đồng ý.',
      soon: 'Quá hạn trong vòng 4 giờ',
      soonHint: 'Nếu không ai quyết định, đề nghị sẽ hết hiệu lực và phải nêu lại.',
      requesters: 'Người đang chờ quyết định',
    },
    dialog: {
      title: 'Thay đổi được đề nghị',
      change: 'Thay đổi được đề nghị',
      resource: 'Áp dụng cho',
      requester: 'Người đề nghị',
      requested: 'Đề nghị lúc',
      expires: 'Quá hạn lúc',
      reason: 'Lý do đề nghị',
      details: 'Những gì sẽ thay đổi',
      status: 'Trạng thái',
      decision: 'Quyết định',
      decidedBy: 'Người quyết định',
      decidedAt: 'Quyết định lúc',
      decisionReason: 'Lý do đã nêu',
      yourReason: 'Lý do của bạn',
      yourReasonPlaceholder: 'Bạn đã kiểm tra những gì trước khi quyết định?',
      reasonRequired: 'Phải nêu lý do. Lý do được lưu cùng quyết định.',
      approve: 'Đồng ý thay đổi',
      reject: 'Từ chối thay đổi',
      working: 'Đang ghi nhận…',
      confirmApprove:
        'Đồng ý thay đổi này? Thay đổi có hiệu lực ngay khi bạn xác nhận và tên bạn sẽ gắn với nó.',
      confirmReject: 'Từ chối thay đổi này? Người đề nghị sẽ phải nêu lại.',
      done: 'Đã ghi nhận.',
      close: 'Đóng',
    },
  },

  ownerType: {
    CUSTOMER: 'Khách hàng',
    SYSTEM: 'Thành phần nền tảng',
  },

  credentialStatus: {
    ACTIVE: 'Đang hoạt động',
    REVOKED: 'Đã thu hồi',
    EXPIRED: 'Đã hết hạn',
  },

  credentials: {
    title: 'Thông tin xác thực API',
    subtitle:
      'Các khoá mà khách hàng và thành phần nền tảng dùng để kết nối tới nhà đăng ký, cùng những gì mỗi khoá được phép làm.',
    loadError: 'Không tải được thông tin xác thực API',
    empty: 'Không có thông tin xác thực nào khớp với bộ lọc này.',
    count: '{{count}} thông tin xác thực',
    searchPlaceholder: 'Tên khách hàng',
    filterOwner: 'Thuộc về',
    filterStatus: 'Trạng thái',
    col: {
      owner: 'Thuộc về',
      key: 'Tiền tố khoá',
      scopes: 'Được phép',
      created: 'Ngày cấp',
      lastUsed: 'Lần dùng cuối',
      state: 'Trạng thái',
    },
    keyHint: 'Vài ký tự đầu của khoá, đủ để nhận ra khoá trong một dòng nhật ký.',
    noScopes: 'Không gì cả',
    neverUsed: 'Chưa dùng lần nào',
    expiresAt: 'Hết hạn {{when}}',
    expiredAt: 'Đã hết hạn {{when}}',
    revokedAt: 'Đã thu hồi {{when}}',
    noExpiry: 'Không hết hạn',
    component: 'Thành phần nền tảng',
    componentHint: 'Do phần mềm của chính nhà đăng ký dùng, không phải khách hàng dùng.',
    openOwner: 'Mở {{name}}',
    stat: {
      active: 'Khoá đang hoạt động',
      idle: 'Không dùng trong 90 ngày',
      idleHint: 'Nên thu hồi những khoá không ai dùng.',
      revoked: 'Đã thu hồi hoặc hết hạn',
      owners: 'Tài khoản có khoá',
    },
  },

  auditOutcome: {
    SUCCESS: 'Đã xong',
    DENIED: 'Bị từ chối',
    FAILED: 'Thất bại',
  },

  actorType: {
    OPERATOR: 'Nhân viên',
    CUSTOMER: 'Khách hàng',
    RESELLER: 'Đại lý',
    SYSTEM: 'Tự động',
    EXTERNAL: 'Bên ngoài',
  },

  resourceType: {
    domain: 'Tên miền',
    user: 'Người',
    customer: 'Khách hàng',
    reseller: 'Đại lý',
    subject: 'Chủ thể',
    subject_version: 'Bản ghi chủ thể',
    subject_identifier: 'Số định danh',
    subject_verification: 'Kiểm tra định danh',
    dossier: 'Hồ sơ',
    document: 'Tài liệu',
    document_version: 'Tài liệu',
    abuse_case: 'Vụ việc lạm dụng',
    domain_dispute: 'Tranh chấp',
    disclosure_case: 'Yêu cầu cung cấp dữ liệu',
    law_enforcement_request: 'Yêu cầu của cơ quan chức năng',
    domain_action: 'Tác vụ tên miền',
    registry_transaction: 'Lệnh tới cơ quan đăng ký',
    ledger_account: 'Ví',
    auction: 'Đấu giá',
    drop: 'Tên miền dự kiến rơi',
    escrow_deposit: 'Bản nộp lưu ký',
    vnnic_report_batch: 'Báo cáo VNNIC',
    vnnic_dossier_sync: 'Đồng bộ hồ sơ VNNIC',
    vnnic_sync_transaction: 'Đồng bộ VNNIC',
    registrant_change: 'Thay đổi chủ thể',
    ops_incident: 'Sự cố',
    approval_request: 'Đề nghị phê duyệt',
    api_credential: 'Khoá API',
    reporting_consumer: 'Nơi nhận dữ liệu',
    backorder: 'Đặt giữ tên miền',
    domain_transfer: 'Chuyển nhà đăng ký',
    notification: 'Thông báo',
    notify_owner: 'Cấu hình thông báo',
    provider_setting: 'Cấu hình nhà cung cấp',
    module_instance: 'Kết nối module',
    addon: 'Tiện ích',
    einvoice_document: 'Hoá đơn điện tử',
    reconciliation_issue: 'Mục đối soát',
    registry_connection: 'Kết nối registry',
    registry_poll_message: 'Thư poll registry',
    subject_birth_date: 'Ngày sinh',
    vn_place_list: 'Danh mục tỉnh/phường',
    webhook_delivery: 'Lần gửi webhook',
    webhook_endpoint: 'Điểm nhận webhook',
    work_item: 'Việc cần xử lý',
    declaration: 'Bản khai',
    declaration_check_settings: 'Cài đặt kiểm tra bản khai',
    document_type: 'Loại hồ sơ',
    domain_tracking_report: 'Báo cáo theo dõi tên miền hằng ngày',
    dns_zone: 'Vùng DNS',
    payment_session: 'Phiên cổng thanh toán',
  },

  audit: {
    title: 'Nhật ký kiểm toán',
    subtitle: 'Ai đã làm gì, lúc nào và vì sao, trên toàn bộ nền tảng.',
    loadError: 'Không tải được nhật ký kiểm toán',
    empty: 'Không có bản ghi nào khớp với bộ lọc này.',
    count: '{{count}} bản ghi',
    filterAction: 'Hành động',
    filterActionPlaceholder: 'ví dụ: domain. hoặc approval.decide',
    filterOutcome: 'Kết quả',
    filterActor: 'Người thực hiện',
    filterResourceType: 'Loại bản ghi',
    filterResourceId: 'Bản ghi',
    filterResourceIdPlaceholder: 'Mã bản ghi chính xác',
    filterCorrelation: 'Mã tương quan',
    filterCorrelationPlaceholder: 'Nhóm mọi việc thuộc cùng một thao tác',
    col: {
      time: 'Thời điểm',
      actor: 'Ai',
      action: 'Hành động',
      resource: 'Bản ghi',
      outcome: 'Kết quả',
      reason: 'Lý do',
    },
    automated: 'Tự động',
    noReason: 'Không ghi nhận lý do',
    open: 'Mở bản ghi này',
    stat: {
      entries: 'Bản ghi hiển thị',
      refused: 'Bị từ chối',
      refusedHint: 'Có người đã thử làm việc mà họ không được phép làm.',
      failed: 'Thất bại',
      failedHint: 'Hành động được phép nhưng không hoàn tất.',
      people: 'Người liên quan',
    },
    drawer: {
      title: 'Bản ghi kiểm toán',
      when: 'Thời điểm',
      who: 'Ai',
      accountKind: 'Tài khoản',
      action: 'Hành động',
      actionCode: 'Mã hành động (kỹ thuật)',
      technical: 'Chi tiết kỹ thuật',
      record: 'Bản ghi',
      recordKind: 'Loại bản ghi',
      outcome: 'Kết quả',
      reason: 'Lý do đã nêu',
      before: 'Trước',
      after: 'Sau',
      noChange: 'Không có giá trị nào được ghi cho bản ghi này.',
      correlation: 'Mã tương quan',
      request: 'Mã yêu cầu',
      id: 'Mã bản ghi',
      close: 'Đóng',
    },
  },
};
